import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiExternalLink, FiCheck, FiInfo } = FiIcons;

const PricingSection = () => {
  // Bundled pricing (License + Hosted by 3CX) based on the new 3CX Pricing Table
  const pricingMatrix = [
    { userRange: '16–40', maxUsers: 40, simultaneousCalls: 8, basicPrice: 595, proPrice: 690, aiPrice: 870 },
    { userRange: '41–80', maxUsers: 80, simultaneousCalls: 16, basicPrice: 1050, proPrice: 1245, aiPrice: 1545 },
    { userRange: '81–120', maxUsers: 120, simultaneousCalls: 24, basicPrice: 1275, proPrice: 1545, aiPrice: 1945 },
    { userRange: '121–192', maxUsers: 192, simultaneousCalls: 32, basicPrice: 1810, proPrice: 2145, aiPrice: 2590 },
    { userRange: '193–288', maxUsers: 288, simultaneousCalls: 48, basicPrice: 2295, proPrice: 2790, aiPrice: 3290 }
  ];

  const [selectedUsers, setSelectedUsers] = useState(24);
  const [selectedEdition, setSelectedEdition] = useState('pro');
  const [currentPricing, setCurrentPricing] = useState(pricingMatrix[0]);

  useEffect(() => {
    const tier = pricingMatrix.find(tier => {
      const [min, max] = tier.userRange.split('–').map(Number);
      return selectedUsers >= min && selectedUsers <= max;
    }) || pricingMatrix[0];
    
    setCurrentPricing(tier);
  }, [selectedUsers]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const currentPrice = 
    selectedEdition === 'basic' ? currentPricing.basicPrice :
    selectedEdition === 'pro' ? currentPricing.proPrice : 
    currentPricing.aiPrice;

  // Calculate percentage for slider background filling
  const sliderPercentage = ((selectedUsers - 16) / (288 - 16)) * 100;

  return (
    <section id="pricing-section" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="w-4/5 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            3CX Cloud PBX Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Flexible pricing that scales with your business. Choose the perfect plan for your needs.
          </p>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <div className="mb-8">
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                Approximate User Count: <span className="text-primary-600">{selectedUsers}</span>
              </label>
              <div className="relative">
                <input
                  type="range"
                  min="16"
                  max="288"
                  value={selectedUsers}
                  onChange={(e) => setSelectedUsers(Number(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #2563eb 0%, #2563eb ${sliderPercentage}%, #e5e7eb ${sliderPercentage}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between mt-2 text-sm text-gray-600">
                  <span>16</span>
                  <span>80</span>
                  <span>120</span>
                  <span>192</span>
                  <span>288</span>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                Choose Edition
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setSelectedEdition('basic')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    selectedEdition === 'basic'
                      ? 'border-primary-600 bg-primary-50 shadow-lg transform scale-105'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Basic</h3>
                  <p className="text-gray-600 text-sm">Core features for smaller teams</p>
                  <div className="mt-4">
                    <span className="text-2xl font-bold text-primary-600">
                      {formatPrice(currentPricing.basicPrice)}
                    </span>
                    <span className="text-gray-500">/year</span>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedEdition('pro')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    selectedEdition === 'pro'
                      ? 'border-primary-600 bg-primary-50 shadow-lg transform scale-105'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
                  <p className="text-gray-600 text-sm">Advanced routing & reporting</p>
                  <div className="mt-4">
                    <span className="text-2xl font-bold text-primary-600">
                      {formatPrice(currentPricing.proPrice)}
                    </span>
                    <span className="text-gray-500">/year</span>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedEdition('ai')}
                  className={`p-6 rounded-xl border-2 transition-all ${
                    selectedEdition === 'ai'
                      ? 'border-primary-600 bg-primary-50 shadow-lg transform scale-105'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">AI Edition</h3>
                  <p className="text-gray-600 text-sm">Next-gen AI capabilities</p>
                  <div className="mt-4">
                    <span className="text-2xl font-bold text-primary-600">
                      {formatPrice(currentPricing.aiPrice)}
                    </span>
                    <span className="text-gray-500">/year</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Pricing Display */}
            <motion.div
              className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-white"
              key={`${selectedUsers}-${selectedEdition}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-primary-200 text-sm mb-2">Recommended Users</div>
                  <div className="text-2xl font-bold">{currentPricing.userRange}</div>
                </div>
                <div>
                  <div className="text-primary-200 text-sm mb-2">Max Users</div>
                  <div className="text-2xl font-bold">{currentPricing.maxUsers}</div>
                </div>
                <div>
                  <div className="text-primary-200 text-sm mb-2">Simultaneous Calls</div>
                  <div className="text-2xl font-bold">{currentPricing.simultaneousCalls}</div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-primary-500">
                <div className="text-center">
                  <div className="text-primary-200 text-sm mb-2">Annual Price (License + Hosting)</div>
                  <div className="text-4xl font-bold mb-4">{formatPrice(currentPrice)}</div>
                  
                  <a
                    href="https://www.3cx.com/ordering/pricing/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-white text-primary-600 px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    <span>View Feature Comparison</span>
                    <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Hosting and Custom License Note */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 text-center">
                <div className="flex items-center justify-center mb-1">
                  <SafeIcon icon={FiInfo} className="h-4 w-4 mr-2 text-primary-600 flex-shrink-0" />
                  <span>All pricing shown includes full hosting by 3CX. If you are looking for a direct license without hosting, please contact us.</span>
                </div>
                <div className="mt-2 font-medium text-gray-900 italic">
                  If you need a bigger license count than listed above, we can build to suit your needs - contact us today.
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Key Features Included</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">Unlimited Extensions</div>
                    <div className="text-sm text-gray-600">Create as many user extensions as needed</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">Web & Mobile Apps</div>
                    <div className="text-sm text-gray-600">Work from anywhere with native apps</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">Video Conferencing</div>
                    <div className="text-sm text-gray-600">Built-in video meetings and screen sharing</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">Call Recording</div>
                    <div className="text-sm text-gray-600">Record calls for compliance and training</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">CRM Integration</div>
                    <div className="text-sm text-gray-600">Connect with popular CRM systems</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">24/7 Support</div>
                    <div className="text-sm text-gray-600">Round-the-clock technical assistance</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          background: #2563eb;
          cursor: pointer;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          background: #2563eb;
          cursor: pointer;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .slider:focus {
          outline: none;
        }
      `}</style>
    </section>
  );
};

export default PricingSection;